/** @fileoverview Normalizes `data-skill-read` payloads coming from the chat stream and API. */

import { isRecord } from '../lib/utils';
import type { SkillReadData } from '../types/chat';

/** Generic status copy that must never be rendered as a skill name (e.g. "Consulted guidance"). */
const PLACEHOLDER_SKILL_NAMES = new Set([
  'consulted guidance',
  'guidance',
  'skill',
  'read skill',
  'reading skill',
]);

const normalizeSkillName = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().replace(/\s+/g, ' ');
  if (!trimmed || PLACEHOLDER_SKILL_NAMES.has(trimmed.toLowerCase())) return null;
  return trimmed;
};

/**
 * `skillName` is the source of truth; a generic `label` is only used when it reads like a
 * real skill name. When neither is usable the name stays null so the UI can say
 * "Used a skill" instead of "Used skill Skill".
 */
export const toSkillReadData = (value: unknown): SkillReadData | null => {
  if (!isRecord(value)) return null;
  const { id, phase } = value;
  if (typeof id !== 'string' || !id) return null;
  if (phase !== 'started' && phase !== 'completed' && phase !== 'failed') return null;
  return {
    id,
    phase,
    skillName: normalizeSkillName(value.skillName) ?? normalizeSkillName(value.label),
  };
};
